import axios from 'axios'

import { Observation } from '../../../../api/observations'

/** True when an axios error is HTTP 404. */
export function isAxios404(err: unknown): boolean {
  if (err == null || typeof err !== 'object') return false
  return axios.isAxiosError(err) && err.response?.status === 404
}

/** Resolve observation id from common tag payload shapes. */
export function resolveTagObservationId(tag: {
  observation?: unknown
  observation_id?: unknown
}): number | string | null {
  const raw = tag.observation ?? tag.observation_id
  if (raw == null || raw === '') return null
  if (typeof raw === 'number' || typeof raw === 'string') return raw
  if (typeof raw === 'object' && raw !== null && 'id' in raw) {
    const nestedId = (raw as { id?: unknown }).id
    if (typeof nestedId === 'number' || typeof nestedId === 'string') {
      return nestedId
    }
  }
  return null
}

export function observationIdFromTag(tag: {
  observation?: unknown
  observation_id?: unknown
}): number | null {
  const raw = resolveTagObservationId(tag)
  if (raw == null) return null
  const n = typeof raw === 'number' ? raw : Number(raw)
  return Number.isFinite(n) ? n : null
}

function calendarDayKey(
  value: string | Date | null | undefined,
): string | null {
  if (value == null || value === '') return null
  const d = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(d.getTime())) return null
  return `${d.getUTCFullYear()}-${d.getUTCMonth() + 1}-${d.getUTCDate()}`
}

function viewpointsImageFromObservation(obs: Observation): {
  id?: unknown
  taken_on?: unknown
} | null {
  const vi = obs?.image?.processed_image?.viewpoints_image
  if (vi && typeof vi === 'object') return vi
  return null
}

/**
 * Whether a SAFETY observation belongs to the currently displayed panorama.
 * Primary: viewpoints_image.id === current image id (same as create vp_image).
 * Fallback: same calendar day on taken_on when nested image id is missing.
 */
export function observationMatchesCurrentImage(
  obs: Observation,
  currentImage: { id?: unknown; taken_on?: unknown },
): boolean {
  const vi = viewpointsImageFromObservation(obs)
  const currentId = currentImage?.id
  if (vi?.id != null && currentId != null && currentId !== '') {
    return String(vi.id) === String(currentId)
  }
  const obsDay = calendarDayKey(
    (vi?.taken_on as string | undefined) ?? undefined,
  )
  const currentDay = calendarDayKey(
    currentImage?.taken_on as string | Date | undefined,
  )
  if (obsDay && currentDay) {
    return obsDay === currentDay
  }
  return false
}
